import { Stack, Box, Theme } from '@mui/material';
import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import Text from '../../text';
import Icon from '../../icon';
import { SidebarListItemProps } from './helpers';
import Menu from '@/components/menu';

const useStyles = createUseStyles((theme: Theme) => ({
  sidebarListItem: {
    cursor: 'pointer',
    width: '100%',
    gap: 8,
    color: theme.palette.text.primary,
    padding: '4px 18px 4px 18px',
    borderRadius: 2,
    position: 'relative',
    '& .active-background': {
      position: 'absolute',
      left: 6,
      right: 6,
      top: 0,
      bottom: 0,
      borderRadius: 2,
      backgroundColor: 'transparent',
      pointerEvents: 'none',
      zIndex: 0,
    },
    '& .content': {
      position: 'relative',
      zIndex: 1,
    },
    '& .title': {
      wordBreak: 'break-word',
      overflow: 'hidden',
      maxHeight: '3em',
    },
    '&:hover .active-background': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.active .active-background': {
      backgroundColor: theme.palette.action.selected,
    },
    '&.menu-open:not(.active) .active-background': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.menu-open.active .active-background': {
      backgroundColor: theme.palette.action.selected,
    },
    '& $menu': {
      opacity: 0,
    },
    '&:hover $menu, &.menu-open $menu': {
      opacity: 1,
    },
  },
  menu: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
  },
}));

const SidebarListItem: FC<SidebarListItemProps> = ({
  title,
  content,
  onClick,
  active = false,
  className = '',
  menuItems = [],
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const styles = useStyles();
  const compClass = classNames(styles.sidebarListItem, className, {
    active,
    'menu-open': menuOpen,
  });
  const hasContent = !!content;
  const hasMenu = !!menuItems.length;

  // prevent clicking on the menu trigger from loading the thread
  const handleClickMenu = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <Stack className={compClass} onClick={() => onClick?.()} role="button">
      <Box className="active-background" aria-hidden />
      <Stack className="content" direction="column" gap={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box flexGrow={1} pr={1}>
            <Text size="small" bold className="title">
              {title}
            </Text>
          </Box>
        </Stack>
        {hasContent && <Text size="x-small">{content}</Text>}
      </Stack>
      {hasMenu && (
        <Box className={styles.menu} role="presentation" onClick={handleClickMenu}>
          <Menu
            trigger={<Icon name="menu-dots-vert" size="small" />}
            menuItems={menuItems}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            minWidth={120}
            onChange={setMenuOpen}
          />
        </Box>
      )}
    </Stack>
  );
};

export default SidebarListItem;
